import { ReactNode } from "react";
import { Button, Drawer, Tooltip } from "@redelair-sa/intiza-ui";
import { AgendaItem } from "../../../services/AgendaService";
import { TranslationService } from "../../../services/TranslationService";
import { formatAgendaMoney, formatLastManagement } from "../utils/agendaFormat";
import { canRunAgendaAction } from "../utils/agendaPermissions";
import { getActionMeta, isCompleted } from "../utils/agendaUtils";
import AgendaLastManagement from "./AgendaLastManagement";
import AgendaPriorityBadge from "./AgendaPriorityBadge";

// Above the app header, below Bootstrap modals (1055) so the confirmation
// modals opened from the drawer stay visible.
const DRAWER_Z_INDEX = 1040;

interface AgendaDrawerProps {
  item?: AgendaItem;
  canEdit: boolean;
  onClose: () => void;
  onPrimaryAction: (item: AgendaItem, subject: string, content: string) => void;
  onToggleCompleted: (item: AgendaItem) => void;
  onDelete: (item: AgendaItem) => void;
}

const Stat = ({
  label,
  className = "",
  children
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className="agenda-drawer__stat">
    <div className="agenda-drawer__stat-label">{label}</div>
    <div className={`agenda-drawer__stat-value ${className}`}>{children}</div>
  </div>
);

const AgendaDrawerBody = ({ item }: { item: AgendaItem }) => {
  const { translate } = TranslationService;
  const meta = getActionMeta(item.accion);
  return (
    <>
      <div className="agenda-drawer__stats">
        <Stat label={translate.AgendaDrawerOverdueDebt} className="text-danger">
          {formatAgendaMoney(item.deudaVencida)}
        </Stat>
        <Stat label={translate.AgendaDrawerTotalDebt}>
          {formatAgendaMoney(item.deuda)}
        </Stat>
        <Stat label={translate.AgendaDrawerPriority}>
          <AgendaPriorityBadge priority={item.prioridad} />
        </Stat>
        <Stat label={translate.AgendaDrawerLastManagement}>
          <AgendaLastManagement
            ultimaGestion={item.ultimaGestion}
            text={formatLastManagement(item.ultimaGestion)}
          />
        </Stat>
      </div>

      <section className="agenda-drawer__section">
        <h3 className="agenda-drawer__section-title">
          {translate.AgendaDrawerWhySuggested}
        </h3>
        <div className="agenda-drawer__reason">{item.motivo}</div>
      </section>

      <section className="agenda-drawer__section">
        <h3 className="agenda-drawer__section-title">
          {translate.AgendaDrawerSuggestedAction}
        </h3>
        <div className="agenda-drawer__text mb-3">{item.accionDetalle}</div>
        {meta.kind === "email" && item.asunto && (
          <div className="mb-3">
            <div className="agenda-drawer__label">{translate.Subject}</div>
            <div className="agenda-drawer__text">{item.asunto}</div>
          </div>
        )}
        {item.contenido && (
          <div>
            <div className="agenda-drawer__label">
              {TranslationService.getTranslation(meta.draftKey)}
            </div>
            {/* `contenido` is LLM output: render it as plain text, never as HTML.
                It is edited in the flow opened by the primary action. */}
            <div className="agenda-drawer__text">{item.contenido}</div>
          </div>
        )}
      </section>
    </>
  );
};

const AgendaDrawerFooter = ({
  item,
  canRunAction,
  canManage,
  onPrimaryAction,
  onToggleCompleted,
  onDelete
}: Pick<
  AgendaDrawerProps,
  "onPrimaryAction" | "onToggleCompleted" | "onDelete"
> & {
  item: AgendaItem;
  /** Permission for the suggested action (email, WhatsApp, task, comment). */
  canRunAction: boolean;
  /** Complete, reopen and delete only depend on the agenda itself. */
  canManage: boolean;
}) => {
  const { translate } = TranslationService;
  const meta = getActionMeta(item.accion);
  return (
    <div className="agenda-drawer__footer">
      {meta.primaryKey && (
        <Button
          variant="primary"
          disabled={!canRunAction}
          onClick={() =>
            onPrimaryAction(item, item.asunto ?? "", item.contenido ?? "")
          }
        >
          {TranslationService.getTranslation(meta.primaryKey)}
        </Button>
      )}
      <Button
        variant="secondary"
        disabled={!canManage}
        onClick={() => onToggleCompleted(item)}
      >
        {isCompleted(item) ? translate.AgendaReopen : translate.AgendaComplete}
      </Button>
      <span className="ms-auto">
        <Tooltip content={translate.AgendaRemoveItem}>
          <Button
            variant="ghost"
            disabled={!canManage}
            aria-label={translate.AgendaRemoveItem}
            onClick={() => onDelete(item)}
            startIcon={
              <i className="fa-regular fa-trash-can" aria-hidden="true" />
            }
          />
        </Tooltip>
      </span>
    </div>
  );
};

/**
 * Right sidebar with the detail of an agenda item. The suggestion is shown as
 * read-only text: the primary action opens the existing flow prefilled with it,
 * where it can be edited before sending.
 */
const AgendaDrawer = ({
  item,
  canEdit,
  onClose,
  onPrimaryAction,
  onToggleCompleted,
  onDelete
}: AgendaDrawerProps) => (
  <Drawer
    open={item !== undefined}
    onClose={onClose}
    side="right"
    width="min(480px, 100vw)"
    zIndex={DRAWER_Z_INDEX}
    showCloseButton
    closeAriaLabel={TranslationService.translate.Close}
    header={<h2 className="agenda-drawer__title">{item?.nombre}</h2>}
    aria-label={item?.nombre ?? undefined}
    footer={
      item ? (
        <AgendaDrawerFooter
          item={item}
          // Without permission for the suggested action the button stays
          // visible but disabled.
          canRunAction={canEdit && canRunAgendaAction(item.accion)}
          canManage={canEdit}
          onPrimaryAction={onPrimaryAction}
          onToggleCompleted={onToggleCompleted}
          onDelete={onDelete}
        />
      ) : undefined
    }
  >
    {item && <AgendaDrawerBody item={item} />}
  </Drawer>
);

export default AgendaDrawer;
